import moment from 'moment'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDashboardContext } from '../../../providers/contexts/Dashboard'
import { getAssociationsService } from '../../../services/middlelayer-dashboard/association'
import {
    PreferenceOptions,
    UserRole,
    signInPreferenceOptions,
} from '../../../utils/constants'
import { AccountSettingsFormsTypes } from '../../../utils/types/common'

export const parsePreferences = (
    optInMarketingByEmail?: boolean,
    optInMarketingByPhone?: boolean
) => {
    if (optInMarketingByEmail && !optInMarketingByPhone) {
        return PreferenceOptions.EMAIL
    }
    if (!optInMarketingByEmail && optInMarketingByPhone) {
        return PreferenceOptions.SMS
    }
    if (optInMarketingByPhone && optInMarketingByEmail) {
        return PreferenceOptions.EMAIL_SMS
    }
    return PreferenceOptions.NONE
}

export const parsePreferenceFlags = (preference: PreferenceOptions) => {
    switch (preference) {
        case PreferenceOptions.EMAIL_SMS: {
            return {
                optInMarketingByEmail: true,
                optInMarketingByPhone: true,
            }
        }
        case PreferenceOptions.EMAIL: {
            return {
                optInMarketingByEmail: true,
                optInMarketingByPhone: false,
            }
        }
        case PreferenceOptions.SMS: {
            return {
                optInMarketingByEmail: false,
                optInMarketingByPhone: true,
            }
        }
        case PreferenceOptions.NONE: {
            return {
                optInMarketingByEmail: false,
                optInMarketingByPhone: false,
            }
        }
    }
}

export const parseSignInPreference = (optInInputOnlyLogin: boolean | null) => {
    if (optInInputOnlyLogin === false || optInInputOnlyLogin === null) {
        return signInPreferenceOptions.EMAIL_AND_SMS
    } else if (optInInputOnlyLogin === true) {
        return signInPreferenceOptions.INPUT_ONLY
    } else throw Error('')
}

export const parseSignInPreferenceToBoolean = (
    preference: signInPreferenceOptions
): {
    optInInputOnlyLogin: boolean
} => {
    switch (preference) {
        case signInPreferenceOptions.EMAIL_AND_SMS:
            return {
                optInInputOnlyLogin: false,
            }
        case signInPreferenceOptions.INPUT_ONLY:
            return {
                optInInputOnlyLogin: true,
            }
        default:
            throw Error('')
    }
}

export const useUser = () => {
    const { userData, userDataLoading, getUserDetail } = useDashboardContext(
        ({ userData, userDataLoading, getUserDetail }) => ({
            userData,
            userDataLoading,
            getUserDetail,
        })
    )

    const [userMap, setUserMap] = useState(new Map())

    const [hasAssociation, setAssociationState] = useState(false)
    const [loadingAssocation, setLoadingAssociationState] = useState(true)

    useEffect(() => {
        if (userData && userData.userRole !== UserRole.Client) {
            setLoadingAssociationState(true)
            getAssociationsService(userData.userRole)
                .then((response) =>
                    setAssociationState(Boolean(response?.confirmed.length))
                )
                .finally(() => {
                    setLoadingAssociationState(false)
                })
        } else {
            setAssociationState(false)
            setLoadingAssociationState(false)
        }
    }, [userData?.userRole])

    const formikInitialValues: AccountSettingsFormsTypes = useMemo(() => {
        return {
            businessName: userData?.businessName ?? '',
            beautyHealthCustomerId: userData?.beautyHealthCustomerId ?? '',
            firstname: userData?.firstName ?? '',
            lastname: userData?.lastName ?? '',
            birthday: userData?.dateOfBirth
                ? moment(new Date(userData.dateOfBirth))
                      .utc()
                      .format('MM/DD/YYYY')
                : undefined,
            mobilenumber: userData?.mobilePhoneNumber ?? '',
            email: userData?.emailAddress ?? '',
            country: userData?.country ?? '',
            userRole: userData?.userRole ?? UserRole.Provider,
            selectPreferences: parsePreferences(
                userData?.optInMarketingByEmail,
                userData?.optInMarketingByPhone
            ),
            signInPreference: parseSignInPreference(
                userData?.optInInputOnlyLogin || null
            ),
        }
    }, [userData])

    useEffect(() => {
        if (userData) {
            setUserMap(
                new Map([
                    ...Object.entries(userData),
                    [
                        'optInPreferences',
                        parsePreferences(
                            userData.optInMarketingByEmail,
                            userData.optInMarketingByPhone
                        ),
                    ],
                    [
                        'signInPreference',
                        parseSignInPreference(userData.optInInputOnlyLogin),
                    ],
                ])
            )
        }
    }, [userData])

    const getUserValue = useCallback(
        (key?: string, defaultValue = '') => {
            return key && userMap.has(key) ? userMap.get(key) : defaultValue
        },
        [userMap]
    )

    const isProvider = useCallback(
        () => getUserValue('userRole') === UserRole.Provider,
        [getUserValue]
    )

    return {
        getUserValue,
        formikInitialValues,
        isProvider,
        getUserDetail,
        userDataLoading: userDataLoading || loadingAssocation,
        hasAssociation,
    } as const
}
